import { Box, Grid, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import DatePickerField from '../../../common/DatePickerField';
import EditIcon from '../../../common/icons/EditIcon';
import SelectField from '../../../common/SelectField';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import { usePermissions } from '../../../../context/PermissionContext';
import type { DropdownOption, UserProfileData } from '../../../../services/types/userProfile.types';
import { formatDate } from '../../../../utils/date';

export interface EmploymentInfoCardProps {
  data: UserProfileData;
  isEditMode: boolean;
  setIsEditMode?: (isEditMode: boolean) => void;
  onChange: (patch: Partial<UserProfileData>) => void;
  departments: DropdownOption[];
  permissionLevels: DropdownOption[];
  employmentTypes: DropdownOption[];
}

const EmploymentInfoCard: React.FC<EmploymentInfoCardProps> = ({
  data,
  isEditMode,
  setIsEditMode,
  onChange,
  departments,
  permissionLevels,
  employmentTypes,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (isEditMode === false) {
      setIsEdit(false);
    }
  }, [isEditMode]);

  const deptOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.name })),
    [departments],
  );
  const permOptions = useMemo(
    () => permissionLevels.map((d) => ({ value: d.id, label: d.name })),
    [permissionLevels],
  );
  const empTypeOptions = useMemo(
    () => employmentTypes.map((d) => ({ value: d.id, label: d.name })),
    [employmentTypes],
  );

  const deptLabel =
    data.departmentId == null ? 'N/A' : departments.find((d) => d.id === data.departmentId)?.name ?? 'N/A';
  const permLabel =
    data.permissionLevel == null
      ? 'N/A'
      : permissionLevels.find((d) => d.id === data.permissionLevel)?.name ?? String(data.permissionLevel);
  const empLabel =
    data.employmentType == null
      ? 'N/A'
      : employmentTypes.find((d) => d.id === data.employmentType)?.name ?? String(data.employmentType);

  return (
    <Box className="view-section" sx={{ bgcolor: '#F1F3FF', borderRadius: '8px', p: '20px', mb: 2.5 }}>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
          {!isEdit && hasPermission(PERMISSION_IDS.USER_PROFILE_EDIT) && (
            <IconButton
              className="view-edit-icon"
              aria-label="Edit employment details"
              onClick={() => {
                setIsEdit(true);
                setIsEditMode?.(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, mb: 2, color: '#101828' }}>Employment details</Typography>
      {!isEdit ? (
        <>
          <Box className="view-row-box">
            <Box className="view-column-box">
              <div className="view-label-title">Department</div>
              <div className="view-value-title">{deptLabel}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Permission level</div>
              <div className="view-value-title">{permLabel}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Employment type</div>
              <div className="view-value-title">{empLabel}</div>
            </Box>
          </Box>
          <Box className="view-row-box" sx={{ mt: 2.5 }}>
            <Box className="view-column-box">
              <div className="view-label-title">Company joined date</div>
              <div className="view-value-title">
                {data.companyJoinedDate ? formatDate(data.companyJoinedDate) : 'N/A'}
              </div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Probation start date</div>
              <div className="view-value-title">
                {data.probationStartDate ? formatDate(data.probationStartDate) : 'N/A'}
              </div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Probation end date</div>
              <div className="view-value-title">
                {data.probationEndDate ? formatDate(data.probationEndDate) : 'N/A'}
              </div>
            </Box>
          </Box>
          <Box className="view-row-box" sx={{ mt: 2.5 }}>
            <Box className="view-column-box">
              <div className="view-label-title">Confirmed date</div>
              <div className="view-value-title">
                {data.confirmationDate ? formatDate(data.confirmationDate) : 'N/A'}
              </div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Resigned date</div>
              <div className="view-value-title">{data.resignedDate ? formatDate(data.resignedDate) : 'N/A'}</div>
            </Box>
            <Box className="view-column-box" />
          </Box>
        </>
      ) : (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectField
              label="Department"
              value={data.departmentId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                const id = v === '' ? null : Number(v);
                const name = id == null ? null : departments.find((d) => d.id === id)?.name ?? null;
                onChange({ departmentId: id, departmentName: name });
              }}
              options={deptOptions}
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectField
              label="Permission level"
              value={data.permissionLevel ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ permissionLevel: v === '' ? null : Number(v) });
              }}
              options={permOptions}
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectField
              label="Employment type"
              value={data.employmentType ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ employmentType: v === '' ? null : Number(v) });
              }}
              options={empTypeOptions}
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DatePickerField
              label="Company joined date"
              value={data.companyJoinedDate ? dayjs(data.companyJoinedDate) : null}
              onChange={(date) => onChange({ companyJoinedDate: date })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DatePickerField
              label="Probation start date"
              value={data.probationStartDate ? dayjs(data.probationStartDate) : null}
              onChange={(date) => onChange({ probationStartDate: date })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DatePickerField
              label="Probation end date"
              value={data.probationEndDate ? dayjs(data.probationEndDate) : null}
              onChange={(date) => onChange({ probationEndDate: date })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DatePickerField
              label="Confirmed date"
              value={data.confirmationDate ? dayjs(data.confirmationDate) : null}
              onChange={(date) => onChange({ confirmationDate: date })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DatePickerField
              label="Resigned date"
              value={data.resignedDate ? dayjs(data.resignedDate) : null}
              onChange={(date) => onChange({ resignedDate: date })}
            />
          </Grid>
        </Grid>
      )}
      </Box>
    </Box>
  );
};

export default EmploymentInfoCard;
